import { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { FileDown, Loader2, Radio, Share2 } from "lucide-react";
import { toast } from "sonner";
import { WhatsAppIcon } from "@/utils/Icons";
import { downloadPreparedPdf, type PreparedPdf } from "./buildReportPdf";
import { PDF_HIDE_ATTR } from "./shareScopeContext";
import ShareWhatsappDialog from "./ShareWhatsappDialog";

export type PreparePdf = (onProgress?: (done: number, total: number) => void) => Promise<PreparedPdf>;

const ITEM =
  "flex cursor-pointer items-center gap-2 rounded-[8px] px-2.5 py-1.5 text-[12px] text-[#0c211b] outline-none data-[highlighted]:bg-[#eaf5f1] data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50";

/**
 * Share → Download PDF / Send on WhatsApp, for one report card, a whole tab
 * or a custom report. `prepare` renders the PDF from what is on screen at the
 * moment it is called, so the file always carries the current filters.
 *
 * `liveLink` adds "Copy live link": a live page rather than a PDF (the Team
 * members view hands out its live board, `/live/<kf-id>`). Whoever opens it
 * signs in with their own Xale login and sees the numbers as they change
 * (owner, 2026-10-28) — never more than their role allows.
 */
export default function ShareMenu({
  title,
  prepare,
  variant = "icon",
  liveLink,
}: {
  title: string;
  prepare: PreparePdf;
  /** "icon" = card header glyph; "pill" = labelled button for page headers */
  variant?: "icon" | "pill";
  /** The URL "Copy live link" copies; omitted = no such item. */
  liveLink?: () => string;
}) {
  const [busy, setBusy] = useState(false);
  const [whatsappOpen, setWhatsappOpen] = useState(false);

  const download = async () => {
    if (busy) return;
    setBusy(true);
    const id = toast.loading("Preparing PDF…");
    try {
      const pdf = await prepare((done, total) => {
        if (total > 1) toast.loading(`Preparing PDF… ${done} of ${total}`, { id });
      });
      downloadPreparedPdf(pdf);
      toast.success(`Downloaded ${pdf.fileName}`, { id });
    } catch (error) {
      toast.error((error as Error)?.message || "Couldn't create the PDF — please try again.", { id });
    } finally {
      setBusy(false);
    }
  };

  const copyLiveLink = (url: string) => {
    void navigator.clipboard.writeText(url).then(
      () =>
        toast.success("Live link copied", {
          description: "It opens this table on its own page, asks for a Xale sign-in, and keeps the numbers up to date.",
        }),
      () => toast.error("Couldn't copy the link — please try again."),
    );
  };

  return (
    // React bubbles portal events through this tree: a click or keystroke in
    // the menu/dialog must not reach a clickable card or row behind it.
    <span
      {...{ [PDF_HIDE_ATTR]: "" }}
      className="inline-flex shrink-0"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <DropdownMenu.Root modal={false}>
        <DropdownMenu.Trigger asChild>
          {variant === "pill" ? (
            <button
              type="button"
              title="Share as PDF"
              className="flex items-center gap-1.5 rounded-full border border-[#d6ebe3] bg-white px-3 py-1.5 text-[12px] font-medium text-[#1e4635] transition-colors hover:bg-[#eaf5f1] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#218760]"
            >
              {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Share2 className="size-3.5" />} Share
            </button>
          ) : (
            <button
              type="button"
              aria-label={`Share ${title}`}
              title="Share as PDF"
              className="rounded-full p-1.5 text-[#697571] transition-colors hover:bg-[#eaf5f1] hover:text-[#051912] focus-visible:outline-2 focus-visible:outline-[#218760]"
            >
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Share2 className="size-4" />}
            </button>
          )}
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="end"
            sideOffset={6}
            className="z-50 min-w-[190px] rounded-[12px] border border-[#e6e8e7] bg-white p-1 shadow-lg"
          >
            <p className="px-2.5 pb-1 pt-1 text-[11px] text-[#9ba3a0]">A full PDF — every row and column, with these filters</p>
            <DropdownMenu.Item disabled={busy} onSelect={() => void download()} className={ITEM}>
              <FileDown className="size-3.5 text-[#218760]" aria-hidden />
              Download PDF
            </DropdownMenu.Item>
            <DropdownMenu.Item disabled={busy} onSelect={() => setWhatsappOpen(true)} className={ITEM}>
              <WhatsAppIcon className="size-3.5 text-[#25D366]" />
              Send on WhatsApp
            </DropdownMenu.Item>
            {liveLink && (
              <>
                <DropdownMenu.Separator className="mx-1 my-1 h-px bg-[#eef0ef]" />
                <p className="px-2.5 pb-1 pt-0.5 text-[11px] text-[#9ba3a0]">This table on its own page, live — opens after sign-in</p>
                <DropdownMenu.Item onSelect={() => copyLiveLink(liveLink())} className={ITEM}>
                  <Radio className="size-3.5 text-[#218760]" aria-hidden />
                  Copy live link
                </DropdownMenu.Item>
              </>
            )}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      {whatsappOpen && (
        <ShareWhatsappDialog title={title} prepare={prepare} onClose={() => setWhatsappOpen(false)} />
      )}
    </span>
  );
}
